import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  AddChangeRequestReviewDocument,
  type AddChangeRequestReviewMutation,
  type AddChangeRequestReviewMutationVariables,
  GetChangeRequestDetailDocument,
} from "~/features/changeRequests/gqls";
import type { ChangeRequestReviewFormData } from "~/features/changeRequests/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseAddChangeRequestReviewArgs {
  changeRequestId: string;
  onSuccess?: () => void;
}

/** Submit a single review (approve/reject/comment) for a change request. Refetches the detail. */
export function useAddChangeRequestReview({ changeRequestId, onSuccess }: UseAddChangeRequestReviewArgs) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [mutate] = useMutation<AddChangeRequestReviewMutation, AddChangeRequestReviewMutationVariables>(
    AddChangeRequestReviewDocument,
  );
  const [processing, setProcessing] = useState(false);

  const submit = useCallback(
    async (data: ChangeRequestReviewFormData): Promise<boolean> => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        const res = await mutate({
          variables: {
            input: { tenantId, changeRequestId, type: data.type, comment: data.comment.trim() || undefined },
          },
          refetchQueries: [{ query: GetChangeRequestDetailDocument, variables: { tenantId, id: changeRequestId } }],
        });
        return match(res.data?.cnutCreateChangeRequestReview)
          .with({ __typename: "ChangeRequestReviewCreateOutput" }, () => {
            toast.success(t("feature.common.createdSuccessfully", { model: "changeRequestReview" }));
            onSuccess?.();
            return true;
          })
          .with({ __typename: "ClientError" }, (e) => {
            toast.error(e.message);
            return false;
          })
          .otherwise(() => {
            toast.error(t("error.unknown"));
            return false;
          });
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [mutate, tenantId, changeRequestId, onSuccess, t],
  );

  return { submit, processing };
}
